import React, { useMemo, useState } from 'react';
import { Button, Empty, Spin, Table } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { TaskHistoryItem } from '../../types/taskHistory';
import { formatTaskHistoryExecutor } from '../../utils/taskHistoryPresentation';
import TaskHistoryCompletedAtCell from './TaskHistoryCompletedAtCell';
import TaskCategoryBadge from './TaskCategoryBadge';
import TaskHistoryDetailDrawer from './TaskHistoryDetailDrawer';
import TaskHistoryObjectLink from './TaskHistoryObjectLink';
import TaskStatusBadge, { resolveTaskHistoryDisplayStatus } from './TaskStatusBadge';

export interface TaskHistoryTableProps {
  items: TaskHistoryItem[];
  loading?: boolean;
}

const TaskHistoryTable: React.FC<TaskHistoryTableProps> = ({ items, loading = false }) => {
  const [detailItem, setDetailItem] = useState<TaskHistoryItem | null>(null);

  const columns = useMemo<ColumnsType<TaskHistoryItem>>(
    () => [
      {
        title: 'Дата выполнения',
        dataIndex: 'completedAt',
        key: 'completedAt',
        width: 108,
        render: (value: string | null) => <TaskHistoryCompletedAtCell completedAt={value} />
      },
      {
        title: 'Задача',
        dataIndex: 'taskTypeLabel',
        key: 'taskTypeLabel',
        ellipsis: true
      },
      {
        title: 'Тип',
        dataIndex: 'taskCategory',
        key: 'taskCategory',
        width: 120,
        render: (_value, record) => (
          <TaskCategoryBadge category={record.taskCategory} label={record.taskCategoryLabel} />
        )
      },
      {
        title: 'Объект',
        dataIndex: 'objectLabel',
        key: 'objectLabel',
        width: 180,
        ellipsis: true,
        render: (_value, record) => <TaskHistoryObjectLink item={record} />
      },
      {
        title: 'Исполнитель',
        dataIndex: 'completedByUserName',
        key: 'completedByUserName',
        width: 140,
        ellipsis: true,
        render: (value: string | null) => formatTaskHistoryExecutor(value)
      },
      {
        title: 'Длительность',
        dataIndex: 'durationFormatted',
        key: 'durationFormatted',
        width: 120,
        render: (_value, record) => record.durationFormatted || record.durationLabel || '—'
      },
      {
        title: 'Статус',
        dataIndex: 'status',
        key: 'status',
        width: 140,
        render: (_status, record) => (
          <TaskStatusBadge status={resolveTaskHistoryDisplayStatus(record)} />
        )
      },
      {
        title: '',
        key: 'actions',
        width: 100,
        fixed: 'right',
        render: (_value, record) => (
          <Button type="link" size="small" onClick={() => setDetailItem(record)}>
            Подробнее
          </Button>
        )
      }
    ],
    []
  );

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
        <Spin />
      </div>
    );
  }

  if (!items.length) {
    return <Empty description="Нет завершённых задач" image={Empty.PRESENTED_IMAGE_SIMPLE} />;
  }

  return (
    <>
      <Table<TaskHistoryItem>
        rowKey="id"
        size="small"
        pagination={false}
        columns={columns}
        dataSource={items}
        scroll={{ x: 1100 }}
      />
      <TaskHistoryDetailDrawer
        item={detailItem}
        open={detailItem != null}
        onClose={() => setDetailItem(null)}
      />
    </>
  );
};

export default TaskHistoryTable;
